<!--
  AscendQE data grid.

  Presentation + interaction only; the parent owns data loading.
    columns:  [{key, title, width?, sortField?, editable?, hidden?, align?}]
    rows:     [{id, ...}]
  Emits: sort({field, order}), page(n), page-size(n), open(row), edit({row, key, value}),
         delete(ids), context(action, row), selection(state), export(kind)

  Column order, widths and visibility persist per `storageKey` in localStorage.
-->
<template>
  <div
    class="aqe-grid"
    :class="{'aqe-grid--compact': density === 'compact'}"
    @keydown.esc="closeMenus"
  >
    <!-- toolbar -->
    <div class="aqe-grid__bar">
      <div class="aqe-grid__count">
        <template v-if="selectedCount">
          <strong>{{ selectedCount }}</strong> selected
          <button type="button" class="aqe-grid__link" @click="clearSelection">
            Clear
          </button>
          <button
            v-if="canDelete"
            type="button"
            class="aqe-grid__btn aqe-grid__btn--danger"
            @click="$emit('delete', selectionIds())"
          >
            <i class="oxd-icon bi-trash3"></i> Delete
          </button>
        </template>
        <template v-else>
          <span
            v-if="loading"
            class="aqe-grid__spinner"
            role="status"
            aria-label="Loading"
          ></span>
          <span>{{ total }} {{ total === 1 ? 'record' : 'records' }}</span>
        </template>
      </div>
      <div class="aqe-grid__tools">
        <div class="aqe-grid__menuwrap">
          <button
            type="button"
            class="aqe-grid__btn"
            aria-haspopup="true"
            :aria-expanded="String(menu === 'columns')"
            @click.stop="toggleMenu('columns')"
          >
            <i class="oxd-icon bi-layout-three-columns"></i> Columns
          </button>
          <div
            v-if="menu === 'columns'"
            class="aqe-grid__menu"
            role="menu"
            @click.stop
          >
            <label
              v-for="c in orderedColumns"
              :key="c.key"
              class="aqe-grid__check"
              role="menuitemcheckbox"
              :aria-checked="String(!hidden[c.key])"
            >
              <input
                type="checkbox"
                :checked="!hidden[c.key]"
                :disabled="visibleColumns.length === 1 && !hidden[c.key]"
                @change="toggleColumn(c.key)"
              />
              {{ c.title }}
            </label>
            <hr />
            <button type="button" class="aqe-grid__mi" @click="resetLayout">
              <i class="oxd-icon bi-arrow-counterclockwise"></i> Reset layout
            </button>
          </div>
        </div>
        <button
          type="button"
          class="aqe-grid__btn aqe-grid__btn--icon"
          :title="density === 'compact' ? 'Comfortable rows' : 'Compact rows'"
          :aria-label="
            density === 'compact' ? 'Comfortable rows' : 'Compact rows'
          "
          @click="setDensity(density === 'compact' ? 'comfortable' : 'compact')"
        >
          <i
            class="oxd-icon"
            :class="
              density === 'compact' ? 'bi-arrows-expand' : 'bi-arrows-collapse'
            "
          ></i>
        </button>
        <div class="aqe-grid__menuwrap">
          <button
            type="button"
            class="aqe-grid__btn"
            aria-haspopup="true"
            :aria-expanded="String(menu === 'export')"
            @click.stop="toggleMenu('export')"
          >
            <i class="oxd-icon bi-download"></i> Export
          </button>
          <div
            v-if="menu === 'export'"
            class="aqe-grid__menu aqe-grid__menu--right"
            role="menu"
            @click.stop
          >
            <button
              type="button"
              class="aqe-grid__mi"
              role="menuitem"
              @click="doExport('csv')"
            >
              <i class="oxd-icon bi-filetype-csv"></i> CSV (this view)
            </button>
            <button
              type="button"
              class="aqe-grid__mi"
              role="menuitem"
              @click="doExport('xls')"
            >
              <i class="oxd-icon bi-file-earmark-spreadsheet"></i> Excel (this
              view)
            </button>
            <button
              type="button"
              class="aqe-grid__mi"
              role="menuitem"
              @click="doExport('csv-all')"
            >
              <i class="oxd-icon bi-cloud-download"></i> CSV (all
              {{ total }} records)
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- select-all-across-pages banner -->
    <div
      v-if="pageFullySelected && total > rows.length && !allMatching"
      class="aqe-grid__banner"
    >
      All {{ rows.length }} on this page are selected.
      <button type="button" class="aqe-grid__link" @click="selectAllMatching">
        Select all {{ total }} records
      </button>
    </div>
    <div v-else-if="allMatching" class="aqe-grid__banner">
      All {{ total - excluded.size }} records are selected.
      <button type="button" class="aqe-grid__link" @click="clearSelection">
        Clear selection
      </button>
    </div>

    <!-- table -->
    <div ref="scroller" class="aqe-grid__scroll">
      <table
        class="aqe-grid__table"
        role="grid"
        :aria-rowcount="total + 1"
        :aria-busy="String(loading)"
        :style="{width: tableWidth + 'px'}"
      >
        <colgroup>
          <col v-if="selectable" style="width: 44px" />
          <col v-if="expandable" style="width: 40px" />
          <col
            v-for="c in visibleColumns"
            :key="c.key"
            :style="{width: widthOf(c) + 'px'}"
          />
          <col v-if="$slots.actions" style="width: 112px" />
        </colgroup>
        <thead>
          <tr role="row">
            <th
              v-if="selectable"
              class="aqe-grid__th aqe-grid__th--check"
              scope="col"
            >
              <input
                type="checkbox"
                class="aqe-grid__cb"
                aria-label="Select all rows on this page"
                :checked="pageFullySelected"
                :indeterminate.prop="pagePartlySelected"
                @change="togglePage($event.target.checked)"
              />
            </th>
            <th v-if="expandable" class="aqe-grid__th" scope="col">
              <span class="aqe-sr">Details</span>
            </th>
            <th
              v-for="(c, i) in visibleColumns"
              :key="c.key"
              class="aqe-grid__th"
              :class="{
                'is-sortable': !!c.sortField,
                'is-dragging': drag.key === c.key,
                'is-drop-before': drag.over === c.key && drag.side === 'before',
                'is-drop-after': drag.over === c.key && drag.side === 'after',
              }"
              scope="col"
              role="columnheader"
              :aria-sort="ariaSort(c)"
              draggable="true"
              @dragstart="onDragStart($event, c)"
              @dragover.prevent="onDragOver($event, c)"
              @dragleave="drag.over = null"
              @drop.prevent="onDrop(c)"
              @dragend="onDragEnd"
            >
              <button
                v-if="c.sortField"
                type="button"
                class="aqe-grid__sort"
                :title="'Sort by ' + c.title + ' (Alt+←/→ to move column)'"
                @click="cycleSort(c)"
                @keydown.alt.left.prevent="moveColumn(c.key, i - 1)"
                @keydown.alt.right.prevent="moveColumn(c.key, i + 1)"
              >
                <span>{{ c.title }}</span>
                <i
                  class="oxd-icon aqe-grid__sorticon"
                  :class="sortIcon(c)"
                  aria-hidden="true"
                ></i>
              </button>
              <span v-else class="aqe-grid__thlabel">{{ c.title }}</span>
              <span
                class="aqe-grid__resizer"
                role="separator"
                aria-orientation="vertical"
                :aria-label="'Resize ' + c.title"
                tabindex="0"
                @mousedown.stop.prevent="startResize($event, c)"
                @dblclick="autoFit(c)"
                @keydown.left.prevent="nudge(c, -16)"
                @keydown.right.prevent="nudge(c, 16)"
                @click.stop
              ></span>
            </th>
            <th v-if="$slots.actions" class="aqe-grid__th" scope="col">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-if="loading && !rows.length">
            <tr
              v-for="n in 6"
              :key="'sk' + n"
              class="aqe-grid__skel"
              aria-hidden="true"
            >
              <td :colspan="colspan"><span></span></td>
            </tr>
          </template>
          <tr v-else-if="!rows.length">
            <td :colspan="colspan" class="aqe-grid__empty">
              <i class="oxd-icon bi-inbox"></i>
              <div>No records found</div>
            </td>
          </tr>
          <template v-for="(row, ri) in rows" :key="row.id">
            <tr
              class="aqe-grid__row"
              :class="{
                'is-selected': isSelected(row),
                'is-active': activeIndex === ri,
                'is-open': expanded[row.id],
              }"
              role="row"
              :aria-rowindex="ri + 2"
              :aria-selected="selectable ? String(isSelected(row)) : undefined"
              :tabindex="activeIndex === ri ? 0 : -1"
              @click="onRowClick($event, row, ri)"
              @dblclick="onRowDblClick($event, row)"
              @contextmenu.prevent="openContext($event, row, ri)"
              @keydown="onRowKey($event, row, ri)"
              @focus="activeIndex = ri"
            >
              <td
                v-if="selectable"
                class="aqe-grid__td aqe-grid__td--check"
                @click.stop
              >
                <input
                  type="checkbox"
                  class="aqe-grid__cb"
                  :aria-label="'Select ' + (row[labelKey] || row.id)"
                  :checked="isSelected(row)"
                  :disabled="row.isSelectable === false"
                  @click="onCheck($event, row, ri)"
                />
              </td>
              <td v-if="expandable" class="aqe-grid__td" @click.stop>
                <button
                  type="button"
                  class="aqe-grid__expander"
                  :aria-expanded="String(!!expanded[row.id])"
                  :aria-label="
                    (expanded[row.id] ? 'Collapse ' : 'Expand ') +
                    (row[labelKey] || 'row')
                  "
                  @click="toggleExpand(row)"
                >
                  <i class="oxd-icon bi-chevron-right"></i>
                </button>
              </td>
              <td
                v-for="c in visibleColumns"
                :key="c.key"
                class="aqe-grid__td"
                :class="{
                  'is-editable': c.editable,
                  'is-editing': isEditing(row, c),
                }"
                :style="{textAlign: c.align || 'left'}"
                role="gridcell"
              >
                <input
                  v-if="isEditing(row, c)"
                  ref="editor"
                  v-model="editValue"
                  class="aqe-grid__editor"
                  :aria-label="'Edit ' + c.title"
                  @click.stop
                  @dblclick.stop
                  @keydown.enter.prevent.stop="commitEdit"
                  @keydown.esc.prevent.stop="cancelEdit"
                  @keydown.tab.prevent.stop="commitEdit"
                  @blur="commitEdit"
                />
                <slot
                  v-else
                  :name="'cell-' + c.key"
                  :row="row"
                  :value="row[c.key]"
                >
                  <span class="aqe-grid__cell" :title="row[c.key]">{{
                    row[c.key] ?? ''
                  }}</span>
                </slot>
              </td>
              <td
                v-if="$slots.actions"
                class="aqe-grid__td aqe-grid__td--actions"
                @click.stop
              >
                <slot name="actions" :row="row"></slot>
              </td>
            </tr>
            <tr
              v-if="expandable && expanded[row.id]"
              class="aqe-grid__detail"
              role="row"
            >
              <td :colspan="colspan">
                <slot name="detail" :row="row"></slot>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- pagination -->
    <div class="aqe-grid__foot">
      <label class="aqe-grid__pagesize">
        Rows per page
        <select
          :value="pageSize"
          @change="$emit('page-size', Number($event.target.value))"
        >
          <option v-for="n in [10, 25, 50, 100]" :key="n" :value="n">
            {{ n }}
          </option>
        </select>
      </label>
      <span class="aqe-grid__range">{{ rangeLabel }}</span>
      <nav class="aqe-grid__pager" aria-label="Pagination">
        <button
          type="button"
          class="aqe-grid__pg"
          :disabled="page <= 1"
          aria-label="First page"
          @click="$emit('page', 1)"
        >
          <i class="oxd-icon bi-chevron-double-left"></i>
        </button>
        <button
          type="button"
          class="aqe-grid__pg"
          :disabled="page <= 1"
          aria-label="Previous page"
          @click="$emit('page', page - 1)"
        >
          <i class="oxd-icon bi-chevron-left"></i>
        </button>
        <button
          v-for="p in pageList"
          :key="p.key"
          type="button"
          class="aqe-grid__pg"
          :class="{'is-current': p.n === page}"
          :disabled="!p.n"
          :aria-current="p.n === page ? 'page' : undefined"
          @click="p.n && $emit('page', p.n)"
        >
          {{ p.n || '…' }}
        </button>
        <button
          type="button"
          class="aqe-grid__pg"
          :disabled="page >= pages"
          aria-label="Next page"
          @click="$emit('page', page + 1)"
        >
          <i class="oxd-icon bi-chevron-right"></i>
        </button>
        <button
          type="button"
          class="aqe-grid__pg"
          :disabled="page >= pages"
          aria-label="Last page"
          @click="$emit('page', pages)"
        >
          <i class="oxd-icon bi-chevron-double-right"></i>
        </button>
      </nav>
    </div>

    <!-- context menu -->
    <teleport to="body">
      <ul
        v-if="context.row"
        ref="ctx"
        class="aqe-grid__ctx"
        role="menu"
        :style="{left: context.x + 'px', top: context.y + 'px'}"
        @keydown="onCtxKey"
      >
        <li v-for="a in contextActions" :key="a.key" role="none">
          <button
            type="button"
            role="menuitem"
            :class="{'is-danger': a.danger}"
            :disabled="a.disabled && a.disabled(context.row)"
            @click="runContext(a)"
          >
            <i class="oxd-icon" :class="'bi-' + a.icon"></i>{{ a.label }}
            <kbd v-if="a.kbd">{{ a.kbd }}</kbd>
          </button>
        </li>
      </ul>
    </teleport>
  </div>
</template>

<script>
const DEFAULT_W = 160;

export default {
  name: 'DataGrid',
  props: {
    columns: {type: Array, required: true},
    rows: {type: Array, default: () => []},
    total: {type: Number, default: 0},
    page: {type: Number, default: 1},
    pageSize: {type: Number, default: 25},
    loading: {type: Boolean, default: false},
    sort: {type: Object, default: () => ({field: null, order: null})},
    selectable: {type: Boolean, default: false},
    expandable: {type: Boolean, default: false},
    canDelete: {type: Boolean, default: false},
    storageKey: {type: String, default: null},
    labelKey: {type: String, default: 'name'},
    contextActions: {type: Array, default: () => []},
  },
  emits: [
    'sort',
    'page',
    'page-size',
    'open',
    'edit',
    'delete',
    'context',
    'export',
    'expand',
  ],

  data() {
    const saved = this.load();
    return {
      order: saved.order || this.columns.map((c) => c.key),
      widths: saved.widths || {},
      hidden:
        saved.hidden ||
        Object.fromEntries(
          this.columns.filter((c) => c.hidden).map((c) => [c.key, true]),
        ),
      density: saved.density || 'comfortable',
      selected: new Set(),
      excluded: new Set(),
      allMatching: false,
      lastChecked: null,
      expanded: {},
      activeIndex: -1,
      editing: null,
      editValue: '',
      menu: null,
      context: {row: null, x: 0, y: 0, index: -1},
      drag: {key: null, over: null, side: null},
      resizing: null,
    };
  },

  computed: {
    orderedColumns() {
      const byKey = Object.fromEntries(this.columns.map((c) => [c.key, c]));
      const known = this.order.filter((k) => byKey[k]);
      const missing = this.columns
        .map((c) => c.key)
        .filter((k) => !known.includes(k));
      return [...known, ...missing].map((k) => byKey[k]);
    },
    visibleColumns() {
      return this.orderedColumns.filter((c) => !this.hidden[c.key]);
    },
    colspan() {
      return (
        this.visibleColumns.length +
        (this.selectable ? 1 : 0) +
        (this.expandable ? 1 : 0) +
        (this.$slots.actions ? 1 : 0)
      );
    },
    tableWidth() {
      return (
        this.visibleColumns.reduce((a, c) => a + this.widthOf(c), 0) +
        (this.selectable ? 44 : 0) +
        (this.expandable ? 40 : 0) +
        (this.$slots.actions ? 112 : 0)
      );
    },
    pages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
    pageList() {
      const out = [];
      const add = (n) => out.push({n, key: 'p' + n});
      const gap = (k) => out.push({n: null, key: 'g' + k});
      const {page, pages} = this;
      for (let n = 1; n <= pages; n++) {
        if (n === 1 || n === pages || Math.abs(n - page) <= 1) add(n);
        else if (out[out.length - 1]?.n !== null) gap(n);
      }
      return out;
    },
    rangeLabel() {
      if (!this.total) return '0 of 0';
      const a = (this.page - 1) * this.pageSize + 1;
      const b = Math.min(this.total, this.page * this.pageSize);
      return `${a}–${b} of ${this.total}`;
    },
    selectableRows() {
      return this.rows.filter((r) => r.isSelectable !== false);
    },
    pageFullySelected() {
      return (
        this.selectableRows.length > 0 &&
        this.selectableRows.every((r) => this.isSelected(r))
      );
    },
    pagePartlySelected() {
      return (
        !this.pageFullySelected &&
        this.selectableRows.some((r) => this.isSelected(r))
      );
    },
    selectedCount() {
      return this.allMatching
        ? this.total - this.excluded.size
        : this.selected.size;
    },
  },

  watch: {
    rows() {
      this.editing = null;
      this.activeIndex = -1;
      this.expanded = {};
    },
  },

  mounted() {
    document.addEventListener('click', this.closeMenus);
    window.addEventListener('scroll', this.closeContext, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeMenus);
    window.removeEventListener('scroll', this.closeContext, true);
    this.stopResize();
  },

  methods: {
    // ── persistence ──
    load() {
      if (!this.storageKey) return {};
      try {
        return JSON.parse(
          localStorage.getItem('aqe.grid.' + this.storageKey) || '{}',
        );
      } catch (e) {
        return {};
      }
    },
    save() {
      if (!this.storageKey) return;
      try {
        localStorage.setItem(
          'aqe.grid.' + this.storageKey,
          JSON.stringify({
            order: this.order,
            widths: this.widths,
            hidden: this.hidden,
            density: this.density,
          }),
        );
      } catch (e) {
        /* storage unavailable */
      }
    },
    resetLayout() {
      this.order = this.columns.map((c) => c.key);
      this.widths = {};
      this.hidden = Object.fromEntries(
        this.columns.filter((c) => c.hidden).map((c) => [c.key, true]),
      );
      this.save();
      this.menu = null;
    },
    setDensity(d) {
      this.density = d;
      this.save();
    },
    widthOf(c) {
      return this.widths[c.key] || c.width || DEFAULT_W;
    },

    // ── menus ──
    toggleMenu(m) {
      this.closeContext();
      this.menu = this.menu === m ? null : m;
    },
    closeMenus() {
      this.menu = null;
      this.closeContext();
    },
    toggleColumn(key) {
      this.hidden = {...this.hidden, [key]: !this.hidden[key]};
      this.save();
    },

    // ── sorting ──
    ariaSort(c) {
      if (!c.sortField || this.sort.field !== c.sortField)
        return c.sortField ? 'none' : undefined;
      return this.sort.order === 'ASC' ? 'ascending' : 'descending';
    },
    sortIcon(c) {
      if (this.sort.field !== c.sortField) return 'bi-arrow-down-up is-idle';
      return this.sort.order === 'ASC' ? 'bi-sort-up' : 'bi-sort-down';
    },
    cycleSort(c) {
      if (this.drag.key) return;
      const order =
        this.sort.field === c.sortField && this.sort.order === 'ASC'
          ? 'DESC'
          : 'ASC';
      this.$emit('sort', {field: c.sortField, order});
    },

    // ── column reorder (drag & drop + Alt+arrows) ──
    onDragStart(e, c) {
      if (this.resizing) {
        e.preventDefault();
        return;
      }
      this.drag.key = c.key;
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', c.key);
    },
    onDragOver(e, c) {
      if (!this.drag.key || this.drag.key === c.key) return;
      const r = e.currentTarget.getBoundingClientRect();
      this.drag.over = c.key;
      this.drag.side = e.clientX < r.left + r.width / 2 ? 'before' : 'after';
    },
    onDrop(c) {
      const from = this.drag.key;
      if (!from || from === c.key) return this.onDragEnd();
      const order = this.order.filter((k) => k !== from);
      let idx = order.indexOf(c.key);
      if (this.drag.side === 'after') idx += 1;
      order.splice(idx, 0, from);
      this.order = order;
      this.save();
      this.onDragEnd();
    },
    onDragEnd() {
      this.drag = {key: null, over: null, side: null};
    },
    moveColumn(key, visibleIndex) {
      const vis = this.visibleColumns.map((c) => c.key);
      if (visibleIndex < 0 || visibleIndex >= vis.length) return;
      const target = vis[visibleIndex];
      const order = this.order.filter((k) => k !== key);
      const ti = order.indexOf(target);
      order.splice(visibleIndex > vis.indexOf(key) ? ti + 1 : ti, 0, key);
      this.order = order;
      this.save();
      this.$nextTick(() => {
        const ths = this.$el.querySelectorAll('.aqe-grid__sort');
        const i = this.visibleColumns
          .filter((c) => c.sortField)
          .findIndex((c) => c.key === key);
        ths[i]?.focus();
      });
    },

    // ── column resize ──
    startResize(e, c) {
      this.resizing = {key: c.key, x: e.clientX, w: this.widthOf(c)};
      document.addEventListener('mousemove', this.onResize);
      document.addEventListener('mouseup', this.stopResize);
      document.body.style.cursor = 'col-resize';
    },
    onResize(e) {
      if (!this.resizing) return;
      const w = Math.max(
        72,
        Math.min(640, this.resizing.w + e.clientX - this.resizing.x),
      );
      this.widths = {...this.widths, [this.resizing.key]: w};
    },
    stopResize() {
      if (this.resizing) this.save();
      this.resizing = null;
      document.removeEventListener('mousemove', this.onResize);
      document.removeEventListener('mouseup', this.stopResize);
      document.body.style.cursor = '';
    },
    nudge(c, d) {
      this.widths = {
        ...this.widths,
        [c.key]: Math.max(72, Math.min(640, this.widthOf(c) + d)),
      };
      this.save();
    },
    autoFit(c) {
      const len = Math.max(
        c.title.length,
        ...this.rows.map((r) => String(r[c.key] ?? '').length),
      );
      this.widths = {
        ...this.widths,
        [c.key]: Math.max(80, Math.min(480, len * 8 + 40)),
      };
      this.save();
    },

    // ── selection ──
    isSelected(row) {
      return this.allMatching
        ? !this.excluded.has(row.id)
        : this.selected.has(row.id);
    },
    setSelected(row, on) {
      if (row.isSelectable === false) return;
      if (this.allMatching) {
        on ? this.excluded.delete(row.id) : this.excluded.add(row.id);
        this.excluded = new Set(this.excluded);
      } else {
        on ? this.selected.add(row.id) : this.selected.delete(row.id);
        this.selected = new Set(this.selected);
      }
    },
    onCheck(e, row, ri) {
      const on = e.target.checked;
      if (e.shiftKey && this.lastChecked !== null) {
        const [a, b] = [this.lastChecked, ri].sort((x, y) => x - y);
        this.rows.slice(a, b + 1).forEach((r) => this.setSelected(r, on));
      } else {
        this.setSelected(row, on);
      }
      this.lastChecked = ri;
    },
    togglePage(on) {
      this.rows.forEach((r) => this.setSelected(r, on));
      if (!on && this.allMatching) this.clearSelection();
    },
    selectAllMatching() {
      this.allMatching = true;
      this.excluded = new Set();
    },
    clearSelection() {
      this.allMatching = false;
      this.selected = new Set();
      this.excluded = new Set();
    },
    /** {allMatching:true, excluded:[...]} or [ids] */
    selectionIds() {
      return this.allMatching
        ? {allMatching: true, excluded: [...this.excluded]}
        : [...this.selected];
    },

    // ── rows ──
    onRowClick(e, row, ri) {
      this.activeIndex = ri;
      if (e.ctrlKey || e.metaKey) {
        this.setSelected(row, !this.isSelected(row));
        return;
      }
      if (e.detail > 1) return;
      clearTimeout(this._clickT);
      // wait to see if this becomes a double-click (inline edit)
      this._clickT = setTimeout(() => this.$emit('open', row), 260);
    },
    onRowDblClick(e, row) {
      clearTimeout(this._clickT);
      const td = e.target.closest('td');
      const ci = td
        ? [...td.parentNode.children].indexOf(td) -
          (this.selectable ? 1 : 0) -
          (this.expandable ? 1 : 0)
        : -1;
      const c = this.visibleColumns[ci];
      if (c && c.editable) this.startEdit(row, c);
    },
    onRowKey(e, row, ri) {
      if (e.target !== e.currentTarget) return;
      const go = (i) => {
        if (i < 0 || i >= this.rows.length) return;
        this.activeIndex = i;
        this.$nextTick(() =>
          this.$el.querySelectorAll('.aqe-grid__row')[i]?.focus(),
        );
      };
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          go(ri + 1);
          if (e.shiftKey) this.setSelected(this.rows[ri + 1] || row, true);
          break;
        case 'ArrowUp':
          e.preventDefault();
          go(ri - 1);
          if (e.shiftKey) this.setSelected(this.rows[ri - 1] || row, true);
          break;
        case 'Home':
          e.preventDefault();
          go(0);
          break;
        case 'End':
          e.preventDefault();
          go(this.rows.length - 1);
          break;
        case ' ':
          e.preventDefault();
          this.setSelected(row, !this.isSelected(row));
          break;
        case 'Enter':
          e.preventDefault();
          this.$emit('open', row);
          break;
        case 'ArrowRight':
          if (this.expandable && !this.expanded[row.id]) {
            e.preventDefault();
            this.toggleExpand(row);
          }
          break;
        case 'ArrowLeft':
          if (this.expandable && this.expanded[row.id]) {
            e.preventDefault();
            this.toggleExpand(row);
          }
          break;
        case 'F2': {
          const c = this.visibleColumns.find((x) => x.editable);
          if (c) {
            e.preventDefault();
            this.startEdit(row, c);
          }
          break;
        }
        case 'ContextMenu': {
          e.preventDefault();
          const r = e.currentTarget.getBoundingClientRect();
          this.openContext(
            {clientX: r.left + 40, clientY: r.bottom - 4},
            row,
            ri,
          );
          break;
        }
        default:
          if (
            (e.ctrlKey || e.metaKey) &&
            e.key.toLowerCase() === 'a' &&
            this.selectable
          ) {
            e.preventDefault();
            this.togglePage(true);
          }
      }
    },
    toggleExpand(row) {
      const open = !this.expanded[row.id];
      this.expanded = {...this.expanded, [row.id]: open};
      if (open) this.$emit('expand', row);
    },

    // ── inline edit ──
    isEditing(row, c) {
      return (
        this.editing && this.editing.id === row.id && this.editing.key === c.key
      );
    },
    startEdit(row, c) {
      this.editing = {id: row.id, key: c.key, row, original: row[c.key] ?? ''};
      this.editValue = row[c.key] ?? '';
      this.$nextTick(() => {
        const el = Array.isArray(this.$refs.editor)
          ? this.$refs.editor[0]
          : this.$refs.editor;
        el?.focus();
        el?.select();
      });
    },
    commitEdit() {
      if (!this.editing) return;
      const {row, key, original} = this.editing;
      const value = String(this.editValue).trim();
      this.editing = null;
      if (value !== String(original)) this.$emit('edit', {row, key, value});
    },
    cancelEdit() {
      this.editing = null;
    },

    // ── context menu ──
    openContext(e, row, ri) {
      if (!this.contextActions.length) return;
      this.menu = null;
      this.activeIndex = ri;
      const x = Math.min(e.clientX, window.innerWidth - 240);
      const y = Math.min(
        e.clientY,
        window.innerHeight - 40 * this.contextActions.length - 16,
      );
      this.context = {row, x, y, index: ri};
      this.$nextTick(() =>
        this.$refs.ctx?.querySelector('button:not(:disabled)')?.focus(),
      );
    },
    closeContext() {
      if (this.context.row) this.context = {row: null, x: 0, y: 0, index: -1};
    },
    runContext(a) {
      const row = this.context.row;
      this.closeContext();
      if (a.key === 'edit-inline') {
        const c = this.visibleColumns.find((x) => x.editable);
        if (c) this.startEdit(row, c);
        return;
      }
      this.$emit('context', a.key, row);
    },
    onCtxKey(e) {
      const items = [
        ...this.$refs.ctx.querySelectorAll('button:not(:disabled)'),
      ];
      const i = items.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        (items[i + 1] || items[0]).focus();
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        (items[i - 1] || items[items.length - 1]).focus();
      }
      if (e.key === 'Escape' || e.key === 'Tab') {
        e.preventDefault();
        const idx = this.context.index;
        this.closeContext();
        this.$el.querySelectorAll('.aqe-grid__row')[idx]?.focus();
      }
    },

    // ── export ──
    doExport(kind) {
      this.menu = null;
      if (kind === 'csv-all')
        return this.$emit('export', {
          kind: 'csv',
          all: true,
          columns: this.visibleColumns,
        });
      this.download(kind, this.rows, this.visibleColumns);
    },
    download(kind, rows, columns) {
      const stamp = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 19);
      const base = (this.storageKey || 'export') + '-' + stamp;
      let blob;
      let name;
      if (kind === 'xls') {
        const esc = (v) =>
          String(v ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
        const head = columns
          .map(
            (c) =>
              `<Cell ss:StyleID="h"><Data ss:Type="String">${esc(
                c.title,
              )}</Data></Cell>`,
          )
          .join('');
        const body = rows
          .map(
            (r) =>
              '<Row>' +
              columns
                .map((c) => {
                  const v = r[c.key];
                  const num =
                    typeof v === 'number' ||
                    (/^-?\d+(\.\d+)?$/.test(String(v ?? '')) &&
                      String(v).length < 15 &&
                      !/^0\d/.test(String(v)));
                  return `<Cell><Data ss:Type="${
                    num ? 'Number' : 'String'
                  }">${esc(v)}</Data></Cell>`;
                })
                .join('') +
              '</Row>',
          )
          .join('');
        const xml = `<?xml version="1.0"?><?mso-application progid="Excel.Sheet"?><Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"><Styles><Style ss:ID="h"><Font ss:Bold="1"/></Style></Styles><Worksheet ss:Name="Export"><Table><Row>${head}</Row>${body}</Table></Worksheet></Workbook>`;
        blob = new Blob([xml], {type: 'application/vnd.ms-excel'});
        name = base + '.xls';
      } else {
        const q = (v) => {
          // Neutralise spreadsheet formulas (CSV injection), and quote every
          // field so embedded CR/LF/;/, can never start a new cell or row.
          let s = String(v ?? '');
          if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
          return '"' + s.replace(/"/g, '""') + '"';
        };
        const csv = [
          columns.map((c) => q(c.title)).join(','),
          ...rows.map((r) => columns.map((c) => q(r[c.key])).join(',')),
        ].join('\r\n');
        blob = new Blob(['﻿' + csv], {type: 'text/csv;charset=utf-8'});
        name = base + '.csv';
      }
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = name;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(a.href);
        a.remove();
      }, 0);
    },
  },
};
</script>

<style lang="scss" scoped>
$ink: #0f172a;
$ink2: #334155;
$ink3: #64748b;
$ink4: #94a3b8;
$line: rgba(15, 23, 42, 0.07);
$indigo: #6366f1;

.aqe-grid {
  font-family: Inter, 'Nunito Sans', sans-serif;
  color: $ink;
  --row-h: 52px;
  &--compact {
    --row-h: 38px;
  }
}
.aqe-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}
.aqe-grid__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 4px 0 12px;
}
.aqe-grid__count {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: $ink2;
  strong {
    color: $ink;
  }
}
.aqe-grid__tools {
  display: flex;
  gap: 6px;
}
.aqe-grid__btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 34px;
  padding: 0 12px;
  border: 0;
  border-radius: 10px;
  background: #fff;
  color: $ink2;
  font: 600 13px Inter, sans-serif;
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.1);
  cursor: pointer;
  &:hover {
    background: #f8fafc;
    color: $ink;
  }
  &--icon {
    width: 34px;
    padding: 0;
    justify-content: center;
  }
  &--danger {
    color: #dc2626;
    box-shadow: 0 0 0 1px rgba(220, 38, 38, 0.3);
    &:hover {
      background: #fef2f2;
      color: #b91c1c;
    }
  }
}
.aqe-grid__link {
  border: 0;
  background: none;
  padding: 0;
  color: $indigo;
  font: 600 13px Inter, sans-serif;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}
.aqe-grid__menuwrap {
  position: relative;
}
.aqe-grid__menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 50;
  min-width: 220px;
  max-height: 340px;
  overflow: auto;
  padding: 6px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.06),
    0 24px 48px -16px rgba(15, 23, 42, 0.28);
  &--right {
    left: auto;
    right: 0;
  }
  hr {
    border: 0;
    border-top: 1px solid $line;
    margin: 6px 0;
  }
}
.aqe-grid__check,
.aqe-grid__mi {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: 0;
  border-radius: 9px;
  background: none;
  color: $ink;
  font: 500 13.5px Inter, sans-serif;
  text-align: left;
  cursor: pointer;
  &:hover {
    background: rgba(15, 23, 42, 0.05);
  }
  i {
    color: $ink3;
  }
}
.aqe-grid__banner {
  margin-bottom: 10px;
  padding: 10px 14px;
  border-radius: 12px;
  background: rgba(99, 102, 241, 0.08);
  color: #3730a3;
  font-size: 13.5px;
  text-align: center;
}
.aqe-grid__scroll {
  position: relative;
  max-height: min(68vh, 720px);
  overflow: auto;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.07);
  overscroll-behavior: contain;
}
.aqe-grid__table {
  min-width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13.5px;
}
.aqe-grid__th {
  position: sticky;
  top: 0;
  z-index: 2;
  height: 42px;
  padding: 0 12px;
  background: rgba(248, 250, 252, 0.96);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid $line;
  text-align: left;
  font: 600 11.5px Inter, sans-serif;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $ink3;
  white-space: nowrap;
  user-select: none;
  &[draggable='true'] {
    cursor: grab;
  }
  &.is-dragging {
    opacity: 0.4;
  }
  &.is-drop-before {
    box-shadow: inset 3px 0 0 $indigo;
  }
  &.is-drop-after {
    box-shadow: inset -3px 0 0 $indigo;
  }
  &--check {
    width: 44px;
    text-align: center;
  }
}
.aqe-grid__sort {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 4px 6px;
  margin-left: -6px;
  border: 0;
  border-radius: 7px;
  background: none;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  cursor: pointer;
  span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &:hover {
    background: rgba(15, 23, 42, 0.05);
    color: $ink;
  }
  [aria-sort='ascending'] &,
  [aria-sort='descending'] & {
    color: $ink;
  }
}
.aqe-grid__sorticon {
  font-size: 12px;
  &.is-idle {
    opacity: 0.35;
  }
}
.aqe-grid__thlabel {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
}
.aqe-grid__resizer {
  position: absolute;
  top: 8px;
  right: 0;
  bottom: 8px;
  width: 9px;
  cursor: col-resize;
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    right: 4px;
    width: 1px;
    background: rgba(15, 23, 42, 0.1);
  }
  &:hover::after,
  &:focus-visible::after {
    width: 3px;
    right: 3px;
    background: $indigo;
    border-radius: 2px;
  }
  &:focus-visible {
    outline: none;
  }
}
.aqe-grid__row {
  height: var(--row-h);
  cursor: pointer;
  outline: none;
  transition: background-color 0.12s;
  &:hover .aqe-grid__td {
    background: #f8fafc;
  }
  &.is-selected .aqe-grid__td {
    background: rgba(99, 102, 241, 0.06);
  }
  &.is-active .aqe-grid__td:first-child,
  &:focus-visible .aqe-grid__td:first-child {
    box-shadow: inset 3px 0 0 $indigo;
  }
  &:focus-visible .aqe-grid__td {
    background: rgba(99, 102, 241, 0.05);
  }
}
.aqe-grid__td {
  padding: 0 12px;
  border-bottom: 1px solid $line;
  color: $ink2;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  &--check {
    text-align: center;
  }
  &--actions {
    text-align: right;
    overflow: visible;
    :deep(.oxd-icon-button) {
      display: inline-flex;
      vertical-align: middle;
      margin-left: 2px;
    }
  }
  &.is-editable {
    cursor: text;
  }
  &.is-editing {
    padding: 0 6px;
  }
}
.aqe-grid__cell {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
}
.aqe-grid__cb {
  width: 16px;
  height: 16px;
  accent-color: $ink;
  cursor: pointer;
}
.aqe-grid__expander {
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 8px;
  background: none;
  color: $ink3;
  cursor: pointer;
  i {
    transition: transform 0.18s;
  }
  &[aria-expanded='true'] i {
    transform: rotate(90deg);
  }
  &:hover {
    background: rgba(15, 23, 42, 0.06);
    color: $ink;
  }
}
.aqe-grid__editor {
  width: 100%;
  height: 34px;
  padding: 0 10px;
  border: 1px solid $indigo;
  border-radius: 9px;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.14);
  font: 500 13.5px Inter, sans-serif;
  color: $ink;
  outline: none;
}
.aqe-grid__detail td {
  padding: 0;
  background: #fbfcfe;
  border-bottom: 1px solid $line;
}
.aqe-grid__empty {
  padding: 56px 0;
  text-align: center;
  color: $ink4;
  i {
    font-size: 28px;
  }
}
.aqe-grid__skel td {
  height: var(--row-h);
  padding: 0 16px;
  border-bottom: 1px solid $line;
  span {
    display: block;
    height: 12px;
    border-radius: 6px;
    background: linear-gradient(90deg, #eef1f5 25%, #f6f8fa 50%, #eef1f5 75%);
    background-size: 200% 100%;
    animation: aqe-shimmer 1.2s infinite;
  }
}
@keyframes aqe-shimmer {
  to {
    background-position: -200% 0;
  }
}
.aqe-grid__spinner {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(15, 23, 42, 0.12);
  border-top-color: $indigo;
  animation: aqe-spin 0.8s linear infinite;
}
@keyframes aqe-spin {
  to {
    transform: rotate(360deg);
  }
}
.aqe-grid__foot {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding-top: 14px;
  font-size: 13px;
  color: $ink3;
}
.aqe-grid__pagesize select {
  margin-left: 8px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 9px;
  background: #fff;
  font: 600 13px Inter, sans-serif;
  color: $ink;
}
.aqe-grid__range {
  margin-right: auto;
}
.aqe-grid__pager {
  display: flex;
  gap: 4px;
}
.aqe-grid__pg {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: $ink2;
  font: 600 13px Inter, sans-serif;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: rgba(15, 23, 42, 0.06);
  }
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
  &.is-current {
    background: $ink;
    color: #fff;
    opacity: 1;
  }
}
</style>

<style lang="scss">
/* teleported context menu (unscoped) */
.aqe-grid__ctx {
  position: fixed;
  z-index: 2000;
  min-width: 220px;
  margin: 0;
  padding: 6px;
  list-style: none;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(16px);
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.06),
    0 24px 48px -16px rgba(15, 23, 42, 0.32);
  font-family: Inter, 'Nunito Sans', sans-serif;
  animation: aqe-ctx-in 0.12s ease-out;
  button {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 10px;
    border: 0;
    border-radius: 9px;
    background: none;
    color: #0f172a;
    font: 500 13.5px Inter, sans-serif;
    text-align: left;
    cursor: pointer;
    i {
      color: #64748b;
    }
    kbd {
      margin-left: auto;
      font: 500 11px Inter, sans-serif;
      color: #94a3b8;
    }
    &:hover,
    &:focus {
      background: rgba(15, 23, 42, 0.05);
      outline: none;
    }
    &.is-danger,
    &.is-danger i {
      color: #dc2626;
    }
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
}
@keyframes aqe-ctx-in {
  from {
    opacity: 0;
    transform: scale(0.98);
  }
}
</style>
